import React, { useContext, useEffect } from "react";
import Container from "@src/components/Container";
import { Button, Heading, Title, theme, DetailText } from "@src/components/nessie-web";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import { PlayIcon } from "@classdojo/web/nessie/icons";
import styled from "@emotion/styled";
import { mediaQueries } from "@src/styles/theme";
import { logEvent } from "@src/utils/logClient";
import curveSVG from "@src/assets/images/schools/video-curve.svg";
import { useCreateConvertExperimentEvent } from "@src/utils/experiment";
import { ExternalSwitches } from "@src/utils/experiments/constants";
import { css } from "@emotion/react";

const {
  colors: { dt_taro60 },
} = theme;

const SchoolsHeroSectionContainer = styled.section``;

const SchoolsHeroContent = styled.div`
  padding-top: 54px;
  padding-bottom: 54px;
  display: grid;
  grid-template-columns: 1fr;
`;

const SchoolsHeroTextBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const SchoolsHeroWordmark = styled(Title)`
  font-weight: 800;
  font-size: 23px;
  line-height: 30px;
  letter-spacing: 0.1px;
  text-align: center;
  padding-bottom: ${theme.space.m}px;
  ${mediaQueries[0]} {
    font-size: 44px;
    line-height: 48px;
    letter-spacing: -0.4px;
    padding-bottom: ${theme.space.l}px;
  }
`;

const SchoolHeroTitle = styled(Title)`
  font-weight: 800;
  font-size: 28px;
  line-height: 36px;
  letter-spacing: -0.2px;
  text-align: center;
  padding-bottom: ${theme.space.s}px;
  ${mediaQueries[0]} {
    font-size: 69px;
    line-height: 72px;
    letter-spacing: -1px;
    padding-bottom: ${theme.space.m}px;
  }
`;

const StarBadge = styled.img`
  width: 24px;
  height: 24px;
  margin-bottom: 2px;
  margin-left: 8px;

  ${mediaQueries[0]} {
    width: 30px;
    height: 30px;
    margin-bottom: 4px;
  }
`;

const SchoolsHeroText = styled(Heading)`
  font-size: 18px;
  line-height: 24px;
  text-align: center;
  font-weight: 700;
  padding-bottom: ${theme.space.l}px;
  ${mediaQueries[0]} {
    font-size: 23px;
    line-height: 30px;
    letter-spacing: -0.1px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  width: 100%;

  ${mediaQueries[0]} {
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    justify-content: center;
  }
`;

const SchoolsHeroImageWrapper = styled.div<{ bgImage: string }>`
  position: relative;
  height: 580px;
  width: 100%;
  margin: auto;
  background-image: url(${curveSVG}), url(${(props) => props.bgImage});
  background-size: auto 24px, cover;
  background-repeat: no-repeat;
  background-position: center -1px, right;

  ${mediaQueries[0]} {
    background-size: 102% auto, cover;
    background-position: top center, center;
  }
`;

const VideoPlayButton = styled(Button)`
  position: absolute !important;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0 !important;
  border-radius: 50% !important;

  div {
    padding: 0;
    width: 120px;
    height: 120px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 100%;

    svg {
      width: 46px;
      height: 46px;
    }
  }
`;

type SchoolsHeroSectionProps = {
  hero_wordmark: string;
  hero_title: string;
  hero_text: string;
  hero_button_text: string;
  hero_button_url: string;
  hero_button_contact_text: string;
  hero_button_contact_url: string;
  hero_video_image: any;
  hero_video_url: string;
  star_badge_icon?: any;
  star_badge_alt?: string;
  button_log_event_name?: string;
  params?: any;
  test_variant?: string;
  have_questions: string;
};

const RedesignSchoolsHeroSection: React.FC<SchoolsHeroSectionProps> = ({
  hero_wordmark,
  hero_title,
  hero_text,
  hero_button_text,
  hero_button_url,
  hero_button_contact_text,
  hero_button_contact_url,
  hero_video_image,
  hero_video_url,
  star_badge_icon,
  star_badge_alt,
  button_log_event_name,
  params,
  test_variant,
  have_questions,
}) => {
  const t = useContext(TranslationContext);

  const modalContext = useContext(ModalContext);
  useEffect(() => {
    if ("openVideo" in params) {
      openVideoModal();
    }
  }, []);

  function fireLogEvent() {
    convertExperiment();
    logEvent({
      eventName: "web.external.cds.main_cta_hero.click",
      eventValue: location.href,
      metadata: params,
      experiments: [schoolsPageExperiment],
    });
  }

  function openVideoModal() {
    modalContext.showModal(ModalType.VideoModal, { youtubeID: hero_video_url });
    logEvent({
      eventName: "web.external.cds.open_video.click",
      metadata: params,
      experiments: [schoolsPageExperiment],
    });
  }

  const schoolsPageExperiment = ExternalSwitches.SCHOOLS_PAGE;
  const convertExperiment = useCreateConvertExperimentEvent(schoolsPageExperiment);

  return (
    <SchoolsHeroSectionContainer>
      <Container>
        <SchoolsHeroContent>
          <SchoolsHeroTextBlock>
            <SchoolsHeroWordmark>
              {t.translate(hero_wordmark)}
              {star_badge_icon && (
                <span>
                  <StarBadge src={star_badge_icon.file.publicURL} alt={t.translate(star_badge_alt) as string} />
                </span>
              )}
            </SchoolsHeroWordmark>
            <SchoolHeroTitle>{t.translate(hero_title)}</SchoolHeroTitle>
            <SchoolsHeroText color={dt_taro60}>{t.translate(hero_text)}</SchoolsHeroText>
            <ButtonWrapper>
              <Button href={`${hero_button_url}${test_variant ? `?test=${test_variant}` : ""}`} onClick={fireLogEvent}>
                {t.translate(hero_button_text)}
              </Button>
              <DetailText color="dt_taro50">
                {t.translate(have_questions)}{" "}
                <a
                  href={hero_button_contact_url}
                  onClick={() =>
                    logEvent({
                      eventName: "web.external.cds.click.hero_contact",
                      eventValue: location.href,
                      metadata: params,
                      experiments: [schoolsPageExperiment],
                    })
                  }
                  css={css({ color: theme.colors.dt_taro50, textDecoration: "underline" })}
                >
                  {t.translate(hero_button_contact_text)}
                </a>
              </DetailText>
            </ButtonWrapper>
          </SchoolsHeroTextBlock>
        </SchoolsHeroContent>
      </Container>
      <SchoolsHeroImageWrapper bgImage={hero_video_image.file.publicURL}>
        <VideoPlayButton kind="tertiary" icon={<PlayIcon />} onClick={openVideoModal} />
      </SchoolsHeroImageWrapper>
    </SchoolsHeroSectionContainer>
  );
};

export default RedesignSchoolsHeroSection;
